import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import {
  SUBSCRIPTION_TIERS,
  type SubscriptionTier,
} from "@/lib/subscriptionTiers";

type PricingCard =
  | (SubscriptionTier & {
      kind: "selfServe";
      cta: string;
      href: string;
    })
  | {
      kind: "enterprise";
      id: "enterprise";
      name: string;
      priceLabel: string;
      quota: string;
      blurb: string;
      features: string[];
      popular: boolean;
      cta: string;
      href: string;
      secondaryCta: { label: string; href: string };
    };

const ENTERPRISE_TIER: PricingCard = {
  kind: "enterprise",
  id: "enterprise",
  name: "Enterprise",
  priceLabel: "Custom",
  quota: "Custom catalog coverage",
  blurb: "For aggregators, agencies, and portfolios with complex operating needs.",
  popular: false,
  features: [
    "Hundreds of brands",
    "Unlimited ASINs",
    "SSO",
    "Dedicated customer success manager",
    "SLA and custom integrations",
    "Volume onboarding",
  ],
  cta: "Book a call",
  href: "https://cal.com/maximiliang/demo",
  secondaryCta: { label: "or email us", href: "mailto:maxagiraldo@gmail.com" },
};

const tiers: PricingCard[] = [
  ...SUBSCRIPTION_TIERS.map((tier) => ({
    ...tier,
    kind: "selfServe" as const,
    cta: "Start free trial",
    href: `/start-trial?plan=${tier.id}`,
  })),
  ENTERPRISE_TIER,
];

function ctaClass(highlighted: boolean) {
  return highlighted
    ? "mt-8 block w-full rounded-full bg-slate-900 px-4 py-2.5 text-center font-medium text-white transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
    : "mt-8 block w-full rounded-full border border-slate-300 px-4 py-2.5 text-center font-medium text-slate-900 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800";
}

export function Pricing({ hideHeader = false }: { hideHeader?: boolean }) {
  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      {!hideHeader && (
        <div className="mb-12 text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl dark:text-slate-100">
            Continuous protection, priced by catalog size.
          </h2>
          <p className="mt-3 text-slate-600 dark:text-slate-400">
            Review removal is one workflow inside always-on reputation monitoring.
          </p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={
              tier.popular
                ? "relative rounded-2xl border-2 border-slate-900 bg-white p-6 shadow-sm dark:border-slate-100 dark:bg-slate-900"
                : "rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900"
            }
          >
            {tier.popular && (
              <div className="absolute -top-3 left-6 rounded-full bg-gradient-accent px-3 py-0.5 text-xs font-medium text-white">
                Most popular
              </div>
            )}

            <h3 className="font-medium text-slate-900 dark:text-slate-100">
              {tier.name}
            </h3>
            {tier.kind === "enterprise" ? (
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
                  {tier.priceLabel}
                </span>
              </div>
            ) : (
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-semibold text-slate-900 dark:text-slate-100">
                  ${tier.priceMonthly.toLocaleString()}
                </span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  /mo
                </span>
              </div>
            )}
            <p className="mt-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              {tier.quota}
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              {tier.blurb}
            </p>

            <ul className="mt-6 space-y-2">
              {tier.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  {feature}
                </li>
              ))}
            </ul>

            {tier.kind === "enterprise" ? (
              <a
                href={tier.href}
                target="_blank"
                rel="noreferrer"
                className={ctaClass(tier.popular)}
              >
                {tier.cta}
              </a>
            ) : (
              <Link to={tier.href} className={ctaClass(tier.popular)}>
                {tier.cta}
              </Link>
            )}

            {tier.kind === "enterprise" && (
              <a
                href={tier.secondaryCta.href}
                className="mt-3 block text-center text-sm text-slate-500 underline-offset-4 hover:text-slate-900 hover:underline dark:text-slate-400 dark:hover:text-slate-100"
              >
                {tier.secondaryCta.label}
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
